// app/(auth)/register/register-form.tsx
// Register Form Client Component with Enhanced UX
// Features: Password strength indicator, show/hide password, real-time validation

"use client"

import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Eye, EyeOff, Check, X } from "lucide-react"
import { signUp } from "@/app/actions/auth"
import { signUpSchema } from "@/lib/validations/auth"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { CardContent } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"

type SignUpFormData = {
  email: string
  password: string
  confirmPassword: string
}

/**
 * Calcula a força da senha e retorna os requisitos atendidos
 */
function calculatePasswordStrength(password: string) {
  const checks = {
    minLength: password.length >= 8,
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[^a-zA-Z0-9]/.test(password),
    hasLetter: /[a-zA-Z]/.test(password),
  }

  const strength = Object.values(checks).filter(Boolean).length
  const percentage = (strength / 4) * 100

  return { checks, strength, percentage }
}

/**
 * Componente de requisitos de senha
 */
function PasswordRequirements({ password }: { password: string }) {
  const { checks } = calculatePasswordStrength(password)

  const requirements = [
    { key: "minLength" as const, label: "Mínimo 8 caracteres", met: checks.minLength },
    { key: "hasLetter" as const, label: "Pelo menos uma letra", met: checks.hasLetter },
    { key: "hasNumber" as const, label: "Pelo menos um número", met: checks.hasNumber },
    { key: "hasSpecial" as const, label: "Pelo menos um caractere especial", met: checks.hasSpecial },
  ]

  return (
    <div className="space-y-1.5 text-xs">
      {requirements.map((req) => (
        <div key={req.key} className="flex items-center gap-2">
          {req.met ? (
            <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
          ) : (
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          <span
            className={cn(
              "transition-colors",
              req.met ? "text-green-600 dark:text-green-400" : "text-muted-foreground"
            )}
          >
            {req.label}
          </span>
        </div>
      ))}
    </div>
  )
}

export function RegisterForm() {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [serverError, setServerError] = useState<string | undefined>()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    mode: "onChange", // Validação em tempo real
  })

  const password = watch("password", "")
  const confirmPassword = watch("confirmPassword", "")
  const passwordStrength = calculatePasswordStrength(password)

  const onSubmit = async (data: SignUpFormData) => {
    setIsSubmitting(true)
    setServerError(undefined)

    try {
      const formData = new FormData()
      formData.append("email", data.email)
      formData.append("password", data.password)
      formData.append("confirmPassword", data.confirmPassword)

      const result = await signUp(undefined, formData)

      // If there's an error, show it. Otherwise, signUp will redirect to confirm-email page
      if (result?.error) {
        setServerError(result.error)
        setIsSubmitting(false)
      }
      // If no error, signUp redirects to /register/confirm-email, so we don't need to handle success here
    } catch (error) {
      // IMPORTANTE: Em Next.js 15, redirect() lança um erro especial NEXT_REDIRECT
      // que deve ser re-lançado para que o redirect funcione corretamente
      if (error instanceof Error && error.message === "NEXT_REDIRECT") {
        throw error
      }
      
      // Também verificar pelo digest do erro (alternativa usada pelo Next.js)
      if (error && typeof error === "object" && "digest" in error) {
        const digest = (error as { digest?: string }).digest
        if (digest?.startsWith("NEXT_REDIRECT")) {
          throw error
        }
      }
      
      setServerError("Erro ao criar conta. Tente novamente.")
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CardContent className="space-y-4">
        {/* Mensagens do servidor */}
        {serverError && (
          <Alert variant="destructive">
            <AlertDescription>{serverError}</AlertDescription>
          </Alert>
        )}

        {/* Campo Email */}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="assessor@exemplo.com"
            aria-invalid={errors.email ? "true" : "false"}
            {...register("email")}
          />
          {errors.email && (
            <p className="text-xs text-destructive">{errors.email.message}</p>
          )}
        </div>

        {/* Campo Senha com funcionalidades avançadas */}
        <div className="space-y-2">
          <div className="space-y-1">
            <Label htmlFor="password">Senha</Label>
            <p className="text-xs text-muted-foreground">
              Sua senha deve conter no mínimo 8 caracteres, incluindo letras, números e caracteres especiais.
            </p>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              className="pr-10"
              aria-invalid={errors.password ? "true" : "false"}
              {...register("password")}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>

          {/* Erro de validação da senha */}
          {errors.password && (
            <p className="text-xs text-destructive">{errors.password.message}</p>
          )}

          {/* Campo Confirmação de Senha - acima dos requisitos */}
          <div className="space-y-2 pt-2">
            <Label htmlFor="confirmPassword">Confirmar Senha</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="••••••••"
                className="pr-10"
                aria-invalid={errors.confirmPassword ? "true" : "false"}
                {...register("confirmPassword")}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showConfirmPassword ? "Ocultar confirmação de senha" : "Mostrar confirmação de senha"}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>

            {/* Feedback visual de correspondência */}
            {confirmPassword && password && (
              <div className="flex items-center gap-2 text-xs">
                {password === confirmPassword ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                    <span className="text-green-600 dark:text-green-400">
                      As senhas coincidem
                    </span>
                  </>
                ) : (
                  <>
                    <X className="h-3.5 w-3.5 text-destructive" />
                    <span className="text-destructive">
                      As senhas não coincidem
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Erro de validação */}
            {errors.confirmPassword && (
              <p className="text-xs text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>

          {/* Instruções e requisitos de senha - sempre visíveis */}
          <div className="space-y-2 rounded-md border bg-muted/30 p-3">
            <p className="text-xs font-medium text-foreground mb-2">
              Requisitos da senha:
            </p>
            <PasswordRequirements password={password || ""} />
            
            {/* Indicador de força da senha - aparece quando há texto */}
            {password && (
              <div className="pt-2 border-t">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    Força:
                  </span>
                  <Progress
                    value={passwordStrength.percentage}
                    className={cn(
                      "h-1.5 flex-1",
                      passwordStrength.percentage < 50 && "[&>div]:bg-red-500",
                      passwordStrength.percentage >= 50 && passwordStrength.percentage < 100 && "[&>div]:bg-amber-500",
                      passwordStrength.percentage === 100 && "[&>div]:bg-green-500"
                    )}
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap min-w-[60px] text-right font-medium">
                    {passwordStrength.percentage < 50 && "Fraca"}
                    {passwordStrength.percentage >= 50 && passwordStrength.percentage < 100 && "Média"}
                    {passwordStrength.percentage === 100 && "Forte"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Botão de submit */}
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "Criando conta..." : "Criar Conta"}
        </Button>
      </CardContent>
    </form>
  )
}
